/**
 * Agent Utilities
 *
 * Helper functions for the multi-agent system:
 * - Extract source summaries (with entity names + column names) from external tools
 * - Format summaries and results for prompt variables
 *
 * Prompts themselves live in backend/.prompts/agent-main/ and agent-source-query/
 * and are loaded via the existing promptLoader.
 */

import type { SourceSummary, SourceAgentResult, EntityDetail } from './types';
import type { ExternalTool } from '../services/tool-executor-service';

// ============================================
// Source Summary Extraction
// ============================================

/**
 * Build source summaries from external tool definitions.
 * Parses tool descriptions to extract entity names AND column names
 * so the main agent can route accurately to the right source.
 */
export function buildSourceSummaries(externalTools: ExternalTool[]): SourceSummary[] {
	return externalTools.map(tool => {
		const description = tool.description || '';
		const type = extractSourceType(tool.id);
		const entityDetails = extractEntityDetails(description);
		const dataContext = extractDataContext(description, tool.name, type);

		return {
			id: extractSourceId(tool.id),
			name: tool.name,
			type,
			description: dataContext,
			entityDetails,
			toolId: tool.id,
		};
	});
}

// ============================================
// Formatting for Prompt Variables
// ============================================

/**
 * Format source summaries into a string for {{SOURCE_SUMMARIES}} variable.
 * Includes entity names with their column names for accurate routing.
 */
export function formatSummariesForPrompt(summaries: SourceSummary[]): string {
	return summaries.map((s, idx) => {
		const totalRows = s.entityDetails.reduce((sum, e) => sum + (e.rowCount || 0), 0);
		const rowInfo = totalRows > 0 ? ` (~${totalRows.toLocaleString()} total rows)` : '';

		let entitiesBlock = '';
		if (s.entityDetails.length > 0) {
			entitiesBlock = '\n' + s.entityDetails.map(e => {
				const rows = e.rowCount ? ` (${e.rowCount.toLocaleString()} rows)` : '';
				const sum = e.summary ? ` — ${e.summary}` : '';
				const cols = e.columns.length > 0 ? `: ${e.columns.join(', ')}` : '';
				return `  - ${e.name}${rows}${sum}${cols}`;
			}).join('\n');
		}

		return `${idx + 1}. **${s.name}** (tool: ${s.toolId}, type: ${s.type})${rowInfo}\n  ${s.description}${entitiesBlock}`;
	}).join('\n\n');
}

/**
 * Format source agent results into a string for {{SOURCE_RESULTS}} variable.
 */
export function formatSourceResultsForPrompt(sourceResults: SourceAgentResult[]): string {
	return sourceResults.map(r => {
		if (!r.success) {
			return `### ${r.sourceName} (FAILED)\nError: ${r.error}`;
		}

		const limitWarning = r.metadata.isLimited
			? `\n⚠️ Data is LIMITED: showing ${r.metadata.rowsReturned} of ${r.metadata.totalRowsMatched} total matching rows. Aggregations may be partial.`
			: '';

		const dataPreview = JSON.stringify(r.data.slice(0, 10), null, 2);
		return `### ${r.sourceName} (${r.metadata.rowsReturned} rows, ${r.metadata.executionTimeMs}ms)${limitWarning}\nQuery: ${r.metadata.queryExecuted || 'N/A'}\nData:\n${dataPreview}`;
	}).join('\n\n');
}

// ============================================
// Entity Detail Extraction (name + columns)
// ============================================

/**
 * Extract entity details (name, row count, column names) from a tool description.
 * Handles all source types:
 * - Database (postgres/mysql/mssql): "• schema.table [~N rows] - desc:\n  col(TYPE), ..."
 * - Excel/CSV: "• SheetName [~N rows]:\n  Col Name(TYPE), ..."
 * - REST API: "Endpoint: /path\n  field(TYPE), ..."
 * - Table catalog: "tableName(1.2K rows), tableName2(500 rows), ..."
 */
function extractEntityDetails(description: string): EntityDetail[] {
	const details: EntityDetail[] = [];
	const seenNames = new Set<string>();

	// 1. Parse detailed bullet-point entities (with columns)
	const bulletSections = description.split(/(?=•\s)/);

	for (const section of bulletSections) {
		if (!section.trim().startsWith('•')) continue;

		// Grab the full header line up to the ":\n" that precedes the column list —
		// the one fixed anchor in formatEntityForLLM's template regardless of whether
		// this entity has a row count or a summary (either can be absent, e.g. a
		// source whose row-count extraction failed still renders "• name:\n  cols").
		// Matches: "• public.table_name [~N rows] — summary:" or just "• table_name:"
		const headerLineMatch = section.match(/•\s+(?:\w+\.)?([^\n]+?):\s*\n/);
		if (!headerLineMatch) continue;
		let header = headerLineMatch[1];

		// Row count is an optional "[~1,234 rows]" bracket anywhere in the header.
		const rowMatch = header.match(/\[~?([\d,]+)\s*rows?\]/);
		const rowCount = rowMatch ? parseInt(rowMatch[1].replace(/,/g, ''), 10) : undefined;
		if (rowMatch && rowMatch.index !== undefined) {
			header = (header.slice(0, rowMatch.index) + header.slice(rowMatch.index + rowMatch[0].length)).trim();
		}

		// What's left is "name" optionally followed by " — summary" / " - summary"
		// (rendered by formatEntityForLLM's entity-level semantic.summary/description
		// fallback). Gives the main agent routing-level meaning without per-field detail.
		const descSplit = header.match(/^(.+?)\s+[—-]\s+(.+)$/);
		const name = (descSplit ? descSplit[1] : header).trim();
		const summary = descSplit ? descSplit[2].trim() : undefined;
		if (!name) continue;

		const columns = extractColumnNames(section);

		details.push({
			name,
			rowCount: rowCount && rowCount > 0 ? rowCount : undefined,
			columns,
			summary,
		});
		seenNames.add(name.toLowerCase());
	}

	// 2. Parse table catalog entries: "tableName(1.2K rows)" or "schema.tableName(500 rows)"
	//    These appear in "All N tables..." or "Other tables..." sections
	const catalogMatch = description.match(/(?:All \d+ tables|Other tables|Remaining \d+ tables)[^:]*:\n?([\s\S]*?)(?:\n\n|$)/i);
	if (catalogMatch) {
		const catalogText = catalogMatch[1];
		// Match: name(Nrows) where N can be "1.2K", "500", "1.5M", etc.
		const entryPattern = /([\w.]+)\(([\d.]+[KMB]?)\s*rows?\)/gi;
		let match;
		while ((match = entryPattern.exec(catalogText)) !== null) {
			const fullName = match[1];
			// Extract short name (after last dot)
			const name = fullName.includes('.') ? fullName.split('.').pop()! : fullName;
			if (seenNames.has(name.toLowerCase())) continue; // Skip duplicates from detailed section

			// Parse abbreviated row count
			const rowStr = match[2];
			let rowCount = 0;
			if (rowStr.endsWith('B')) rowCount = parseFloat(rowStr) * 1_000_000_000;
			else if (rowStr.endsWith('M')) rowCount = parseFloat(rowStr) * 1_000_000;
			else if (rowStr.endsWith('K')) rowCount = parseFloat(rowStr) * 1_000;
			else rowCount = parseInt(rowStr, 10);

			details.push({
				name,
				rowCount: rowCount > 0 ? Math.round(rowCount) : undefined,
				columns: [], // Catalog entries have no column details
			});
			seenNames.add(name.toLowerCase());
		}
	}

	// 3. If no bullet entities found, try REST API endpoint pattern
	if (details.length === 0) {
		const endpointPattern = /Endpoint:\s*(\S+)/g;
		let match;
		while ((match = endpointPattern.exec(description)) !== null) {
			const columns = extractColumnNames(description);
			details.push({
				name: match[1].trim(),
				columns,
			});
		}
	}

	return details;
}

/**
 * Extract column/field names from a description block.
 * Matches patterns like: column_name(TYPE), "Column Name"(TYPE), etc.
 * TYPE is one of: NUMBER, TEXT, BOOLEAN, TIMESTAMP, DATE, INTEGER, FLOAT, etc.
 *
 * For TEXT columns with sample values (low cardinality), includes the values
 * in the output so the main agent can write accurate filter intents.
 * Format: "POD Status{Verified|Not Verified|POD Issues}" instead of just "POD Status"
 *
 * Also captures an optional trailing " — <description>" that formatEntityForLLM
 * (backend) appends per column from the semantic model's field-level summary/unit/
 * notes, e.g. "Acct Assignment Cat.(TEXT){K|Q|P} — Q, N, and P = capex procurement."
 * The description can itself contain commas, so its end is bounded by the START of
 * the NEXT matched column (or end of block) rather than by the next comma.
 */
function extractColumnNames(block: string): string[] {
	const columns: string[] = [];
	const seen = new Set<string>();

	// Match: name(TYPE) optionally followed by [...]{sample1|sample2|...}
	// Captures: [1]=column name, [2]=type, [3]=optional sample values
	const colPattern = /(?:^|,)\s*([^,()\[\]{}\n]+?)\s*\((?:NUMBER|TEXT|BOOLEAN|TIMESTAMP|INTEGER|FLOAT|DECIMAL|DATE|BIGINT|VARCHAR|CHAR|DOUBLE|REAL|ARRAY|OBJECT)(?:[^)]*)\)(?:\[[^\]]*\])?\{([^}]+)\}|(?:^|,)\s*([^,()\[\]{}\n]+?)\s*\((?:NUMBER|TEXT|BOOLEAN|TIMESTAMP|INTEGER|FLOAT|DECIMAL|DATE|BIGINT|VARCHAR|CHAR|DOUBLE|REAL|ARRAY|OBJECT)(?:[^)]*)\)/gi;
	const matches = [...block.matchAll(colPattern)];

	for (let i = 0; i < matches.length; i++) {
		const match = matches[i];
		const col = (match[1] || match[3] || '').trim();
		const samples = match[2] || '';

		// Sanity checks: not too long, not a bullet marker, not already seen
		if (!col || col.length >= 60 || col.includes('•') || seen.has(col)) continue;

		// Include sample values for low-cardinality TEXT columns (≤10 samples)
		const sampleValues = samples.split('|').map(s => s.trim()).filter(Boolean);
		let colStr = sampleValues.length > 0 && sampleValues.length <= 10
			? `${col}{${sampleValues.join('|')}}`
			: col;

		const matchEnd = (match.index ?? 0) + match[0].length;
		const nextStart = i + 1 < matches.length ? (matches[i + 1].index ?? block.length) : block.length;
		const tail = block.slice(matchEnd, nextStart);
		const descMatch = tail.match(/^\s*[—-]\s*([^\n]+)/);
		if (descMatch) {
			const desc = descMatch[1].trim().replace(/,\s*$/, '').slice(0, 200);
			if (desc) colStr += ` — ${desc}`;
		}

		columns.push(colStr);
		seen.add(col);
	}

	return columns;
}

// ============================================
// Description / Context Extraction
// ============================================

/**
 * Extract the most useful description for routing from a tool description.
 * Priority: "Data Context:" line > file name > generated fallback
 */
function extractDataContext(description: string, name: string, type: string): string {
	// Database sources: use "Data Context:" line
	const contextMatch = description.match(/Data Context:\s*(.+?)(?:\n|$)/);
	if (contextMatch) return contextMatch[1].trim();

	// Excel: extract file name
	const excelMatch = description.match(/Excel file:\s*(.+?)\)/);
	if (excelMatch) return `Excel file: ${excelMatch[1].trim()})`;

	// CSV: extract file name
	const csvMatch = description.match(/CSV file:\s*(.+?)\)/);
	if (csvMatch) return `CSV file: ${csvMatch[1].trim()})`;

	// REST API / GraphQL: use first line or "Use this source for" line
	const useForMatch = description.match(/Use this source for[^:]*:\s*(.+?)(?:\n|$)/);
	if (useForMatch) return useForMatch[1].trim();

	// Fallback
	const typeLabels: Record<string, string> = {
		postgres: 'PostgreSQL database',
		mysql: 'MySQL database',
		mssql: 'SQL Server database',
		excel: 'Excel spreadsheet',
		csv: 'CSV file',
		rest_api: 'REST API',
		graphql: 'GraphQL API',
	};
	return `${typeLabels[type] || type}: ${name}`;
}

// ============================================
// ID / Type Helpers
// ============================================

/**
 * Extract source type from tool ID.
 * Tool IDs: "{type}-{hash}_query" or "{type}-{hash}_read"
 */
function extractSourceType(toolId: string): string {
	const match = toolId.match(/^(\w+)-[a-f0-9]+_/);
	if (match) return match[1];

	const types = ['postgres', 'mysql', 'mssql', 'excel', 'csv', 'rest_api', 'graphql'];
	for (const type of types) {
		if (toolId.toLowerCase().includes(type)) return type;
	}
	return 'unknown';
}

/**
 * Extract source ID from tool ID (strip the action suffix).
 * "postgres-abc123_query" → "postgres-abc123"
 */
function extractSourceId(toolId: string): string {
	return toolId.replace(/_(query|read|call)$/, '');
}
